import { useState } from 'react';
import { Check } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Avatar from '../ui/Avatar';
import type { Professional } from '../NetworkingView';
import './CreateGroupModal.css';

export type CreateGroupModalProps = {
    open: boolean;
    onClose: () => void;
    connections: Professional[];
    onCreate: (name: string, members: Professional[]) => void;
};

function CreateGroupModal({
    open,
    onClose,
    connections,
    onCreate,
}: CreateGroupModalProps) {
    // `open` is used as a reset key so the form state is fresh on every open.
    // This avoids calling setState inside a useEffect.
    return open ? (
        <CreateGroupModalBody
            key={String(open)}
            onClose={onClose}
            connections={connections}
            onCreate={onCreate}
        />
    ) : null;
}

function CreateGroupModalBody({
    onClose,
    connections,
    onCreate,
}: Omit<CreateGroupModalProps, 'open'>) {
    const [groupName, setGroupName] = useState('');
    const [selected, setSelected] = useState<Professional[]>([]);

    const toggle = (p: Professional) => {
        setSelected((prev) =>
            prev.find((m) => m.id === p.id)
                ? prev.filter((m) => m.id !== p.id)
                : [...prev, p],
        );
    };

    const canCreate = groupName.trim().length > 0 && selected.length >= 2;

    const submit = () => {
        if (!canCreate) return;
        onCreate(groupName.trim(), selected);
        onClose();
    };

    return (
        <Modal open={true} onClose={onClose} size="md" ariaLabel="Create new group">
            <Modal.Header onClose={onClose}>Create new group</Modal.Header>
            <Modal.Body>
                <div className="create-group__field">
                    <label htmlFor="new-group-name">Group name</label>
                    <input
                        id="new-group-name"
                        type="text"
                        className="create-group__input"
                        placeholder="Enter group name…"
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                    />
                </div>

                <div className="create-group__field">
                    <label>
                        Add members ({selected.length} selected)
                    </label>
                    <p className="create-group__hint">
                        Select at least 2 connections to create a group
                    </p>

                    {connections.length === 0 ? (
                        <p className="create-group__hint create-group__hint--warning">
                            You need connections to create a group
                        </p>
                    ) : (
                        <div className="create-group__member-list">
                            {connections.map((p) => {
                                const isSelected = !!selected.find((m) => m.id === p.id);
                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        className={`create-group__member ${
                                            isSelected ? 'create-group__member--selected' : ''
                                        }`}
                                        onClick={() => toggle(p)}
                                    >
                                        <Avatar
                                            name={p.name}
                                            online={p.online}
                                            size="sm"
                                        />
                                        <div className="create-group__member-info">
                                            <span className="create-group__member-name">
                                                {p.name}
                                            </span>
                                            <span className="create-group__member-role">
                                                {p.role}
                                            </span>
                                        </div>
                                        <span
                                            className={`create-group__checkbox ${
                                                isSelected
                                                    ? 'create-group__checkbox--checked'
                                                    : ''
                                            }`}
                                        >
                                            {isSelected && <Check size={14} />}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="ghost" size="md" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    size="md"
                    onClick={submit}
                    disabled={!canCreate}
                >
                    Create group
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default CreateGroupModal;
